import { AsnIcon, IOSSimulatorIcon, SignalFilled, TorIcon, UnicornIcon } from '@compass/Icon'
import { AndroidLogo, AppleLogo, JavascriptLogo } from '@compass/Logo'
import { AppRoute } from 'appRoutes'
import { QueryFilterDefinition, QueryFilterMenuSection } from 'components/QueryFilters'
import {
  BOT_PROVIDER_DEFINITIONS,
  categoryOptions,
  confidenceOptions,
  getBotProviderLabel,
  getBotProviderLogo,
  identityOptions,
  KNOWN_BOT_NAMES,
  mergeSelectedOptions,
  uniqueOptions,
  withBotNameIcons,
} from 'features/botDetection'
import { useCurrentSubscription, useCurrentSubscriptionData } from 'features/subscription'
import {
  highRecallIdRegex,
  IPV4_CIDR_REGEX,
  IPV6_CIDR_REGEX,
  requestIdRegex,
  semverRegex,
  visitorIdRegex,
} from 'helpers/regex'
import { FILTER_KEY_TO_APP_PRODUCT } from 'helpers/smartSignalMappings'
import { useWorkspaceEnvironments } from 'hooks/api/environment'
import { useSmartSignalsEnabled } from 'hooks/api/smart_signals'
import mapValues from 'lodash/mapValues'
import {
  Binary,
  Blocks,
  Bot,
  BotOff,
  Box,
  Code,
  Copy,
  EthernetPort,
  Fingerprint,
  Glasses,
  Globe,
  GlobeLock,
  Grid2X2,
  HardDrive,
  HousePlug,
  IdCardLanyardIcon,
  Link,
  MapPinXInside,
  MapPlus,
  MousePointerClick,
  PackageOpen,
  PhoneCall,
  Shield,
  Speech,
  SquareFunction,
  SquareSigma,
  SquareTerminal,
  Stamp,
  Wrench,
  Zap,
} from 'lucide-react'
import { AppProduct, VisitsFilter, VisitsFilterKey } from 'models'
import { ampli } from 'models/ampli'
import { useMemo } from 'react'

import { RequestIdCustomFilterUI } from '../components/VisitHistoryFilterItem/VisitHistoryFilterItem'
import { IdentificationUrlKeys, VisitsFilterView } from '../types'
import { visitFilterLabelMap } from '../utils/const'
import { useFilterSearchParams } from './useFilterSearchParams'

const botProviderOptions = BOT_PROVIDER_DEFINITIONS.map((definition) => {
  const Logo = getBotProviderLogo(definition.slug)

  return {
    id: definition.slug,
    name: getBotProviderLabel(definition.slug),
    icon: <Logo size={16} />,
  }
})

const ATTRIBUTE_FILTER_ANALYTICS_LABELS = {
  [IdentificationUrlKeys.IpAddress]: 'IP address',
  [IdentificationUrlKeys.Url]: 'URL',
  [IdentificationUrlKeys.LinkedId]: 'linkedID',
  [IdentificationUrlKeys.RequestId]: 'requestID',
  [IdentificationUrlKeys.VisitorId]: 'visitorID',
  [IdentificationUrlKeys.ProximityId]: 'proximityID',
  [IdentificationUrlKeys.HighRecallId]: 'highRecallID',
  [IdentificationUrlKeys.Origin]: 'origin',
  [IdentificationUrlKeys.Asn]: 'ASN',
  [IdentificationUrlKeys.Environment]: 'environment',
} satisfies Partial<Record<IdentificationUrlKeys, string>>

export const useVisitsFilter = () => {
  const { filterValues } = useFilterSearchParams()
  const { currentSubscriptionId } = useCurrentSubscription()
  const { data: environments } = useWorkspaceEnvironments(currentSubscriptionId)
  const { subscription } = useCurrentSubscriptionData()
  const highRecallEnabled = subscription?.featureSettings?.highRecallEnabled === true

  const { data: enabledProducts } = useSmartSignalsEnabled(currentSubscriptionId)
  const botDetectionEnabled = enabledProducts?.includes(AppProduct.Botd) === true

  const trackAttributeFilterApplied = (nextFilterValues: Partial<VisitsFilterView>) => {
    const appliedAttributeFilters = Object.entries(ATTRIBUTE_FILTER_ANALYTICS_LABELS)
      .filter(([filterKey]) => filterKey in nextFilterValues)
      .map(([, analyticsLabel]) => analyticsLabel)

    ampli.attributeFilterApplied({
      datasetFocus: 'Identification Events',
      attributeFilters: appliedAttributeFilters,
      filterType: 'Dropdown',
    })
  }

  const disabledFilterKeys = useMemo(() => {
    if (!enabledProducts) {
      // While loading, treat all smart-signal filter keys as disabled to prevent flash
      return new Set(Object.keys(FILTER_KEY_TO_APP_PRODUCT))
    }
    const enabledSet = new Set(enabledProducts)
    return new Set(
      Object.entries(FILTER_KEY_TO_APP_PRODUCT)
        .filter(([, product]) => !enabledSet.has(product))
        .map(([key]) => key)
    )
  }, [enabledProducts])

  // Merge in any already-applied bot name that isn't part of the known/curated list
  // (allowed since this filter accepts custom values) so it stays visible and toggleable.
  const botNameOptions = useMemo(
    () => withBotNameIcons(mergeSelectedOptions(uniqueOptions([...KNOWN_BOT_NAMES]), filterValues?.botName)),
    [filterValues?.botName]
  )

  /**
   * This is the defacto list of available filters.
   * If you want to add a filter, add it here
   */
  const filterItems: Record<VisitsFilterKey, QueryFilterDefinition<VisitsFilter>> = useMemo(
    () => ({
      requestId: {
        id: IdentificationUrlKeys.RequestId,
        label: visitFilterLabelMap[IdentificationUrlKeys.RequestId],
        value: filterValues?.[IdentificationUrlKeys.RequestId] ?? '',
        placeholder: '1701234567890.ABcDef',
        inputType: 'text',
        description: 'Press Enter to search or Esc to cancel',
        tooltip: 'Go to an event by event ID.',
        icon: <Binary size={16} />,
        validate: (id: string) =>
          (id !== '' && requestIdRegex.test(id)) || 'The provided ID is not formatted correctly.',
        renderCustomFilterUI: RequestIdCustomFilterUI,
      },

      proximityId: {
        id: IdentificationUrlKeys.ProximityId,
        label: visitFilterLabelMap[IdentificationUrlKeys.ProximityId],
        value: filterValues?.[IdentificationUrlKeys.ProximityId] ?? '',
        placeholder: 'Ykq4nTqI4aa',
        inputType: 'text',
        description: 'Press Enter to search or Esc to cancel',
        tooltip:
          'Find all events that are detected within the same fixed geographical zone with the same proximity ID.',
        icon: <MapPlus size={16} />,
        validate: (id: string) => (id !== '' && id.length <= 256) || 'Please provide a valid id.',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      environment: {
        id: IdentificationUrlKeys.Environment,
        label: visitFilterLabelMap[IdentificationUrlKeys.Environment],
        value: filterValues?.[IdentificationUrlKeys.Environment] ?? [],
        options: environments as { id: string; name: string }[],
        placeholder: 'Filter environments',
        inputType: 'checklist',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find all events that were reported from the specified environment.',
        icon: <Box size={16} />,
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      sdkPlatform: {
        id: IdentificationUrlKeys.SdkPlatform,
        label: visitFilterLabelMap[IdentificationUrlKeys.SdkPlatform],
        value: filterValues?.[IdentificationUrlKeys.SdkPlatform] ?? '',
        options: [
          { id: 'js', name: 'JavaScript', icon: <JavascriptLogo size={16} /> },
          { id: 'android', name: 'Android', icon: <AndroidLogo size={16} /> },
          { id: 'ios', name: 'iOS', icon: <AppleLogo size={16} /> },
        ],
        inputType: 'radiolist',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find events belonging to a specific SDK platform.',
        icon: <Code size={16} />,
        onApply: (platformName) =>
          ampli.platformFilterApplied({
            platformName: [String(platformName)],
            datasetFocus: 'Identification Events',
            'App Page Title': document.title,
            'App Page URL': window.location.href,
            'App Page Path': AppRoute.IdentificationEvents,
          }),
      },

      sdkVersion: {
        id: IdentificationUrlKeys.SdkVersion,
        label: visitFilterLabelMap[IdentificationUrlKeys.SdkVersion],
        value: filterValues?.[IdentificationUrlKeys.SdkVersion] ?? '',
        placeholder: '3.12.0',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find events belonging to a specific SDK version.',
        icon: <Binary size={16} />,
        validate: (value: string) =>
          semverRegex.test(value) || 'Please provide a valid version number in the format 3.12.0',
      },

      ipAddress: {
        id: IdentificationUrlKeys.IpAddress,
        label: visitFilterLabelMap[IdentificationUrlKeys.IpAddress],
        value: filterValues?.[IdentificationUrlKeys.IpAddress] ?? '',
        placeholder: '127.0.0.1',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip:
          'Find all events originating from the specified IP address, or use CIDR notation to match IP address range.',
        icon: <Globe size={16} />,
        validate: (ip: string) =>
          (ip !== '' && (ip.includes('.') ? IPV4_CIDR_REGEX.test(ip) : IPV6_CIDR_REGEX.test(ip))) ||
          'The entered IP is not formatted correctly. Example: 192.168.1.0/22',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      linkedId: {
        id: IdentificationUrlKeys.LinkedId,
        label: visitFilterLabelMap[IdentificationUrlKeys.LinkedId],
        value: filterValues?.[IdentificationUrlKeys.LinkedId] ?? '',
        placeholder: 'user_23567864',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find all events that were reported with the given linked ID.',
        icon: <Link size={16} />,
        validate: (id: string) => (id !== '' && id.length <= 256) || 'Please provide a valid id.',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      url: {
        id: IdentificationUrlKeys.Url,
        label: visitFilterLabelMap[IdentificationUrlKeys.Url],
        value: filterValues?.[IdentificationUrlKeys.Url] ?? '',
        placeholder: 'https://example.com/login',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find all web and mobile events by reported URL and package identifier, respectively.',
        icon: <MousePointerClick size={16} />,
        validate: (url: string) => (url !== '' && url.length <= 10_000) || 'Please enter a valid URL value.',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      origin: {
        id: IdentificationUrlKeys.Origin,
        label: visitFilterLabelMap[IdentificationUrlKeys.Origin],
        value: filterValues?.[IdentificationUrlKeys.Origin] ?? '',
        placeholder: 'example.com',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find all events that were reported for the given origin. Enter website domain or mobile app bundle.',
        icon: <HardDrive size={16} />,
        validate: (origin: string) =>
          (origin !== '' && origin.length <= 10_000) || 'Please enter a valid origin value.',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      asn: {
        id: IdentificationUrlKeys.Asn,
        label: visitFilterLabelMap[IdentificationUrlKeys.Asn],
        value: filterValues?.[IdentificationUrlKeys.Asn] ?? '',
        placeholder: '64512',
        inputType: 'number',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find events originating from the network with the specified ASN (autonomous system number).',
        icon: <AsnIcon size={16} />,
        validate: (asn: number) => (asn >= 0 && asn <= 4294967295) || 'Please enter a valid ASN number (0-4294967295).',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      visitorId: {
        id: IdentificationUrlKeys.VisitorId,
        label: visitFilterLabelMap[IdentificationUrlKeys.VisitorId],
        value: filterValues?.[IdentificationUrlKeys.VisitorId] ?? '',
        placeholder: 'B4549twNaClX4RDz8TUr',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find all events that were reported with the given visitor ID.',
        icon: <Fingerprint size={16} />,
        validate: (id: string) =>
          (id !== '' && visitorIdRegex.test(id)) || 'The provided ID is not formatted correctly.',
        onApply: (_, { filterValues: nextFilterValues }) => trackAttributeFilterApplied(nextFilterValues),
      },

      highRecallId: {
        id: IdentificationUrlKeys.HighRecallId,
        label: visitFilterLabelMap[IdentificationUrlKeys.HighRecallId],
        value: filterValues?.[IdentificationUrlKeys.HighRecallId] ?? '',
        placeholder: 'gE470f388z1jUgu0KhstDF_hr',
        inputType: 'text',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find all events that were reported with the given High Recall ID.',
        icon: <Fingerprint size={16} />,
        validate: (id: string) =>
          (id !== '' && highRecallIdRegex.test(id)) || 'The provided ID is not formatted correctly.',
      },

      minSuspectScore: {
        id: IdentificationUrlKeys.MinSuspectScore,
        label: visitFilterLabelMap[IdentificationUrlKeys.MinSuspectScore],
        operatorModifier: 'is greater than',
        value: filterValues?.[IdentificationUrlKeys.MinSuspectScore] ?? '',
        placeholder: '',
        inputType: 'number',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find events where the Suspect Score is greater than the provided value.',
        icon: <SquareSigma size={16} />,
        validate: (value: number) => value > 0 || 'Please enter a value greater than 0.',
      },

      bot: {
        id: IdentificationUrlKeys.Bot,
        label: visitFilterLabelMap[IdentificationUrlKeys.Bot],
        value: filterValues?.[IdentificationUrlKeys.Bot] ?? '',
        inputType: 'radiolist',
        options: [
          { name: 'all bots', id: 'all', icon: <Bot size={16} color='hsl(var(--fpds-color-gray-8))' /> },
          { name: 'good bot', id: 'good', icon: <Bot size={16} color='hsl(var(--fpds-color-green-8))' /> },
          { name: 'bad bot', id: 'bad', icon: <Bot size={16} color='hsl(var(--fpds-color-red-8))' /> },
          { name: 'no bot', id: 'none', icon: <BotOff size={16} color='hsl(var(--fpds-color-gray-8))' /> },
        ],
        separatorAfterId: 'all',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find events that were detected as a good bot or a bad bot.',
        icon: <Bot size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Bot),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      botName: {
        id: IdentificationUrlKeys.BotName,
        label: visitFilterLabelMap[IdentificationUrlKeys.BotName],
        value: filterValues?.[IdentificationUrlKeys.BotName] ?? [],
        inputType: 'checklist',
        options: botNameOptions,
        searchableOptions: true,
        allowCustomValue: true,
        placeholder: 'Filter or enter bot name',
        tooltip: 'Find events by bot name.',
        icon: <Bot size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.BotName),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      category: {
        id: IdentificationUrlKeys.BotCategory,
        label: visitFilterLabelMap[IdentificationUrlKeys.BotCategory],
        value: filterValues?.[IdentificationUrlKeys.BotCategory] ?? [],
        inputType: 'checklist',
        options: categoryOptions,
        searchableOptions: true,
        placeholder: 'Filter category',
        tooltip: 'Find events by bot category.',
        icon: <Grid2X2 size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.BotCategory),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      provider: {
        id: IdentificationUrlKeys.BotProvider,
        label: visitFilterLabelMap[IdentificationUrlKeys.BotProvider],
        value: filterValues?.[IdentificationUrlKeys.BotProvider] ?? [],
        inputType: 'checklist',
        options: botProviderOptions,
        searchableOptions: true,
        allowCustomValue: true,
        placeholder: 'Filter or enter provider',
        tooltip: 'Find events by bot provider.',
        icon: <IdCardLanyardIcon size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.BotProvider),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      identity: {
        id: IdentificationUrlKeys.BotIdentity,
        label: visitFilterLabelMap[IdentificationUrlKeys.BotIdentity],
        value: filterValues?.[IdentificationUrlKeys.BotIdentity] ?? [],
        inputType: 'checklist',
        options: identityOptions,
        searchableOptions: true,
        placeholder: 'Filter identity',
        tooltip: 'Find events by bot identity.',
        icon: <Fingerprint size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.BotIdentity),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      confidence: {
        id: IdentificationUrlKeys.BotConfidence,
        label: visitFilterLabelMap[IdentificationUrlKeys.BotConfidence],
        value: filterValues?.[IdentificationUrlKeys.BotConfidence] ?? [],
        inputType: 'checklist',
        options: confidenceOptions,
        searchableOptions: true,
        placeholder: 'Filter confidence',
        tooltip: 'Find events by bot confidence level.',
        icon: <SignalFilled size={16} filledBars={3} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.BotConfidence),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      /* -- Reserved for future use --
      highActivity: {
        id: 'highActivity',
        label: 'High activity',
        value: filterValues?.['highActivity'],
        inputType: 'boolean',
        tooltip: 'Find events that were coming from a high activity device',
        icon: <ClockAlert size={16} />,
      },
      */

      vpn: {
        id: IdentificationUrlKeys.Vpn,
        label: visitFilterLabelMap[IdentificationUrlKeys.Vpn],
        value: filterValues?.[IdentificationUrlKeys.Vpn],
        inputType: 'boolean',
        tooltip: 'Find events where VPN usage was detected.',
        icon: <GlobeLock size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Vpn),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      ipBlocklist: {
        id: IdentificationUrlKeys.IpBlocklist,
        label: visitFilterLabelMap[IdentificationUrlKeys.IpBlocklist],
        value: filterValues?.[IdentificationUrlKeys.IpBlocklist],
        inputType: 'boolean',
        tooltip: 'Find events coming from IP addresses that were found in blocklists.',
        icon: <EthernetPort size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.IpBlocklist),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      proxy: {
        id: IdentificationUrlKeys.ResidentialProxy,
        label: visitFilterLabelMap[IdentificationUrlKeys.ResidentialProxy],
        value: filterValues?.[IdentificationUrlKeys.ResidentialProxy],
        inputType: 'boolean',
        tooltip: 'Find events coming from IP addresses that were detected as residential proxy.',
        icon: <HousePlug size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.ResidentialProxy),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      incognito: {
        id: IdentificationUrlKeys.Incognito,
        label: visitFilterLabelMap[IdentificationUrlKeys.Incognito],
        value: filterValues?.[IdentificationUrlKeys.Incognito],
        inputType: 'boolean',
        tooltip: 'Find events where incognito/private mode was detected.',
        icon: <Glasses size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Incognito),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      tampering: {
        id: IdentificationUrlKeys.BrowserTampering,
        label: visitFilterLabelMap[IdentificationUrlKeys.BrowserTampering],
        value: filterValues?.[IdentificationUrlKeys.BrowserTampering],
        inputType: 'boolean',
        tooltip: 'Find events where browser tampering was detected.',
        icon: <Stamp size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.BrowserTampering),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      /** -- reserved for future use --
      mobileRequestTampering: {
        id: 'mobileRequestTampering',
        label: 'Mobile request tampering',
        value: filterValues?.['mobileRequestTampering'],
        inputType: 'boolean',
        tooltip: 'Find events where mobile request tampering was detected.',
        icon: <ArrowUpDown size={16} />,
      },
      */

      virtualMachine: {
        id: IdentificationUrlKeys.VirtualMachine,
        label: visitFilterLabelMap[IdentificationUrlKeys.VirtualMachine],
        value: filterValues?.[IdentificationUrlKeys.VirtualMachine],
        inputType: 'boolean',
        tooltip: 'Find events where the browser was detected as running inside a virtual machine.',
        icon: <Box size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.VirtualMachine),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      privacySettings: {
        id: IdentificationUrlKeys.PrivacySettings,
        label: visitFilterLabelMap[IdentificationUrlKeys.PrivacySettings],
        value: filterValues?.[IdentificationUrlKeys.PrivacySettings],
        inputType: 'boolean',
        tooltip: 'Find events where the browser has privacy settings turned on.',
        icon: <Shield size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.PrivacySettings),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      developerTools: {
        id: IdentificationUrlKeys.DeveloperTools,
        label: visitFilterLabelMap[IdentificationUrlKeys.DeveloperTools],
        value: filterValues?.[IdentificationUrlKeys.DeveloperTools],
        inputType: 'boolean',
        tooltip: 'Find events where the developer tools were open in the browser.',
        icon: <Wrench size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.DeveloperTools),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      deviceRarity: {
        id: IdentificationUrlKeys.DeviceRarity,
        label: visitFilterLabelMap[IdentificationUrlKeys.DeviceRarity],
        value: filterValues?.[IdentificationUrlKeys.DeviceRarity],
        inputType: 'boolean',
        tooltip: 'Find events where the browser is running on an uncommon device configuration.',
        icon: <UnicornIcon size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.DeviceRarity),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      emulator: {
        id: IdentificationUrlKeys.Emulator,
        label: visitFilterLabelMap[IdentificationUrlKeys.Emulator],
        value: filterValues?.[IdentificationUrlKeys.Emulator],
        inputType: 'boolean',
        tooltip: 'Find events that were coming from an Android emulator.',
        icon: <SquareTerminal size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Emulator),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      simulator: {
        id: IdentificationUrlKeys.Simulator,
        label: visitFilterLabelMap[IdentificationUrlKeys.Simulator],
        value: filterValues?.[IdentificationUrlKeys.Simulator],
        inputType: 'boolean',
        tooltip: 'Find events that were coming from an iOS simulator.',
        icon: <IOSSimulatorIcon size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Simulator),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      clonedApp: {
        id: IdentificationUrlKeys.ClonedApp,
        label: visitFilterLabelMap[IdentificationUrlKeys.ClonedApp],
        value: filterValues?.[IdentificationUrlKeys.ClonedApp],
        inputType: 'boolean',
        tooltip: 'Find events that were coming from a cloned mobile app.',
        icon: <Copy size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.ClonedApp),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      locationSpoofing: {
        id: IdentificationUrlKeys.LocationSpoofing,
        label: visitFilterLabelMap[IdentificationUrlKeys.LocationSpoofing],
        value: filterValues?.[IdentificationUrlKeys.LocationSpoofing],
        inputType: 'boolean',
        tooltip: 'Find events coming from a mobile device where location was spoofed.',
        icon: <MapPinXInside size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.LocationSpoofing),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      mitmAttack: {
        id: IdentificationUrlKeys.MitmAttack,
        label: visitFilterLabelMap[IdentificationUrlKeys.MitmAttack],
        value: filterValues?.[IdentificationUrlKeys.MitmAttack],
        inputType: 'boolean',
        tooltip: 'Find events where a request to Fingerprint was intercepted and modified.',
        icon: <Speech size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.MitmAttack),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      activeCall: {
        id: IdentificationUrlKeys.ActiveCall,
        label: visitFilterLabelMap[IdentificationUrlKeys.ActiveCall],
        value: filterValues?.[IdentificationUrlKeys.ActiveCall],
        inputType: 'boolean',
        tooltip: 'Find events coming from a mobile device that was on an active phone call.',
        icon: <PhoneCall size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.ActiveCall),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      rootApps: {
        id: IdentificationUrlKeys.RootedDevice,
        label: visitFilterLabelMap[IdentificationUrlKeys.RootedDevice],
        value: filterValues?.[IdentificationUrlKeys.RootedDevice],
        inputType: 'boolean',
        tooltip: 'Find events coming from a rooted Android device.',
        icon: <Blocks size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.RootedDevice),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      jailbroken: {
        id: IdentificationUrlKeys.JailbrokenDevice,
        label: visitFilterLabelMap[IdentificationUrlKeys.JailbrokenDevice],
        value: filterValues?.[IdentificationUrlKeys.JailbrokenDevice],
        inputType: 'boolean',
        tooltip: 'Find events coming from a jailbroken iOS device.',
        icon: <PackageOpen size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.JailbrokenDevice),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },

      frida: {
        id: IdentificationUrlKeys.Frida,
        label: visitFilterLabelMap[IdentificationUrlKeys.Frida],
        value: filterValues?.[IdentificationUrlKeys.Frida],
        inputType: 'boolean',
        tooltip: 'Find events coming from a mobile app that was modified by a Frida toolkit.',
        icon: <SquareFunction size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Frida),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },
      torNode: {
        id: IdentificationUrlKeys.Tor,
        label: visitFilterLabelMap[IdentificationUrlKeys.Tor],
        value: filterValues?.[IdentificationUrlKeys.Tor],
        inputType: 'boolean',
        tooltip: 'Detected that the IP address is a known Tor exit node or that the request includes a Tor payload.',
        icon: <TorIcon size={16} />,
        isDisabled: disabledFilterKeys.has(IdentificationUrlKeys.Tor),
        disabledTooltip: 'Not included in your plan. Please visit /plans to request an Enterprise trial.',
      },
    }),
    [disabledFilterKeys, environments, filterValues, botNameOptions]
  )
  const defaultValues = mapValues(filterItems, (item) => item.value) as VisitsFilterView

  /**
   * Used in filter menu with separators
   */
  const filterMenuLayout: QueryFilterMenuSection<VisitsFilter>[] = useMemo(
    () => [
      {
        id: 'asjdhkajsd',
        items: [
          filterItems.requestId,
          filterItems.visitorId,
          ...(highRecallEnabled ? [filterItems.highRecallId] : []),
          filterItems.proximityId,
          filterItems.linkedId,
        ],
      },
      {
        id: 'irtninefiu',
        items: [
          filterItems.environment,
          {
            id: 'clientSdk',
            label: 'Client SDK',
            tooltip: 'Find events belonging to a specific SDK platform and version.',
            icon: <Code size={16} />,
            children: [
              {
                id: 'sdkPlatform',
                items: [
                  {
                    id: 'js',
                    label: 'JavaScript',
                    icon: <JavascriptLogo size={16} />,
                    tooltip: 'Find events generated by the Javascript SDK.',
                  },
                  {
                    id: 'android',
                    label: 'Android',
                    icon: <AndroidLogo size={16} />,
                    tooltip: 'Find events generated by the Android SDK.',
                  },
                  {
                    id: 'ios',
                    label: 'iOS',
                    icon: <AppleLogo size={16} />,
                    tooltip: 'Find events generated by the iOS SDK.',
                  },
                ],
              },
              {
                id: 'clientSdk-iosenfoise',
                items: [filterItems.sdkVersion],
              },
            ],
          },
        ],
      },
      {
        id: 'bnjknhisdu',
        items: [filterItems.ipAddress, filterItems.url, filterItems.origin, filterItems.asn],
      },
      {
        id: 'kndsfiusuf',
        items: [
          {
            id: 'smartSignals',
            label: 'Smart Signals',
            tooltip: 'Filter events by a collection of Smart Signals.',
            icon: <Zap size={16} />,
            children: [
              {
                id: 'smartSignals-kajgduyasd',
                items: [
                  filterItems.minSuspectScore,
                  filterItems.ipBlocklist,
                  filterItems.proxy,
                  filterItems.mitmAttack,
                  filterItems.torNode,
                  filterItems.developerTools,
                ],
              },
              {
                id: 'smartSignals-kudfhkdsdf',
                label: 'Browser',
                items: [
                  filterItems.incognito,
                  filterItems.vpn,
                  filterItems.tampering,
                  filterItems.virtualMachine,
                  filterItems.privacySettings,
                  filterItems.deviceRarity,
                ],
              },
              { id: 'smartSignals-ouwherkjnk', label: 'iOS', items: [filterItems.jailbroken, filterItems.simulator] },
              {
                id: 'smartSignals-kjdfhbhshf',
                label: 'Android',
                items: [filterItems.emulator, filterItems.rootApps, filterItems.clonedApp],
              },
              {
                id: 'smartSignals-wiqnmjejrj',
                label: 'Mobile',
                items: [filterItems.locationSpoofing, filterItems.frida, filterItems.activeCall],
              },
            ],
          },
          ...(botDetectionEnabled
            ? [
                {
                  id: 'botDetection',
                  label: 'Bot Detection',
                  tooltip: 'Filter events by bot detection signals.',
                  icon: <Bot size={16} />,
                  children: [
                    {
                      id: 'botDetection-main',
                      items: [
                        filterItems.bot,
                        filterItems.botName,
                        filterItems.category,
                        filterItems.provider,
                        filterItems.identity,
                        filterItems.confidence,
                      ],
                    },
                  ],
                },
              ]
            : []),
        ],
      },
    ],
    [filterItems, highRecallEnabled, botDetectionEnabled]
  )

  return {
    filterItems,
    defaultValues,
    filterMenuLayout,
    disabledFilterKeys,
  }
}
